import { useDialogStore } from "@shared/common/contexts/dialog/useDialogStore";
import { useCurrentInstanceMetadata } from "@shared/common/contexts/instanceMetadata/useCurrentInstanceMetadata";
import { useKnopClient } from "@opstel/contexts/knopClient/useKnopClient";
import { DATETIME_FORMAT_NO_SECONDS } from "@shared/common/utils/luxonHelpers";
import { Dialog, DialogContentContainer } from "@shared/EDS/components/Dialog";
import { messageToast } from "@shared/EDS/components/MessageToast/messageToastFunctions";
import { useMutation } from "@tanstack/react-query";
import { JSX, useCallback, useId } from "react";

export function SendToDonnaDialog(): JSX.Element {
  const { data: instanceMetadata } = useCurrentInstanceMetadata();
  const sendToDonnaDialogId = useId();
  const openDialog = useDialogStore((state) => state.openDialog);
  const closeDialog = useDialogStore((state) => state.closeDialog);
  const { knopClient } = useKnopClient();

  const closeSendToDonnaDialog = useCallback(() => {
    closeDialog(sendToDonnaDialogId);
  }, [closeDialog, sendToDonnaDialogId]);

  const openSendToDonnaDialog = useCallback(() => {
    openDialog(sendToDonnaDialogId);
  }, [openDialog, sendToDonnaDialogId]);

  const sendToDonnaMutation = useMutation({
    mutationFn: (instanceId: string) => {
      closeSendToDonnaDialog();
      return knopClient.sendToDonna(instanceId);
    },
    onError: (err) => {
      console.error("Error sending to Donna:", err);
      messageToast({
        message: `Het versturen naar Donna is mislukt.`,
        type: "error",
      });
    },
    onSuccess: () =>
      messageToast({
        message: `Het plan is verstuurd naar Donna. Het kan enkele minuten duren voordat het zichtbaar is.`,
      }),
  });

  const sendToDonna = useCallback(() => {
    if (!instanceMetadata) {
      console.error("No instance metadata available for sending to Donna.");
      return;
    }
    sendToDonnaMutation.mutate(instanceMetadata.instanceId);
  }, [instanceMetadata, sendToDonnaMutation]);

  return (
    <>
      <tractie-button
        data-testid="send-to-donna-open-button"
        role="button"
        onClick={openSendToDonnaDialog}
        level="primary"
        size="m"
      >
        <tractie-icon size="m" name="send" />
        Versturen naar Donna
      </tractie-button>
      <Dialog
        dialogId={sendToDonnaDialogId}
        title="Versturen naar Donna"
        maxWidth="496px"
        description="Weet je zeker dat je het plan wil versturen naar Donna?"
      >
        <DialogContentContainer>
          <div className="flex flex-col gap-s px-5">
            <h5 className="m-0 text-m leading-m font-medium text-emphasis">Plan</h5>
            <div className="grid auto-rows-fr grid-cols-[1fr_4fr] gap-x-s gap-y-xs">
              <span className="text-s leading-l font-medium text-subtle">Periode</span>
              <div>
                {instanceMetadata?.scenarioStart.toFormat(DATETIME_FORMAT_NO_SECONDS)} t/m{" "}
                {instanceMetadata?.scenarioEnd.toFormat(DATETIME_FORMAT_NO_SECONDS)}
              </div>
              <span className="text-s leading-l font-medium text-subtle">Knoop</span>
              <div>{instanceMetadata?.location}</div>
            </div>
          </div>
          <div className="flex justify-end gap-xs px-l pt-l pb-0 [&>button]:px-5 [&>button]:py-xs">
            <tractie-button
              data-testid="send-to-donna-cancel-button"
              role="button"
              level="tertiary"
              size="m"
              onClick={closeSendToDonnaDialog}
            >
              Annuleren
            </tractie-button>
            <tractie-button
              data-testid="send-to-donna-confirm-button"
              role="button"
              level="primary"
              size="m"
              onClick={sendToDonna}
              autoFocus
            >
              Versturen
            </tractie-button>
          </div>
        </DialogContentContainer>
      </Dialog>
    </>
  );
}
